import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	lazy,
	Suspense,
} from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Search, X } from 'lucide-react';
import './Navbar.css';
import { getFormationALaCarteTrainings } from '../blog-section/formationALaCarteTrainings';

const PreInscriptionModal = lazy(
	() => import('../shared/pre-inscription-modal/PreInscriptionModal'),
);

const FORMATION_A_LA_CARTE_URI = '/formations/formation-a-la-carte';
const CALENDRIER_URI = '/calendrier';
const MAX_SEARCH_RESULTS = 3;

const SEARCHABLE_PAGES = [
	{ path: '/', fallbackTitle: 'Accueil' },
	{ path: '/qui-sommes-nous', fallbackTitle: 'À propos' },
	{ path: '/formations', fallbackTitle: 'Nos formations' },
	{ path: '/formations/agentic-ia', fallbackTitle: 'Agentic IA' },
	{ path: '/formations/it-sales', fallbackTitle: 'IT Sales' },
	{
		path: '/formations/formation-a-la-carte',
		fallbackTitle: 'Formation à la carte',
	},
	{ path: '/calendrier', fallbackTitle: 'Calendrier' },
	{ path: '/contact', fallbackTitle: 'Contact' },
];

const MAIN_COURSE_EVENTS = [
	{
		id: 'agentic-ai-main',
		title: 'Formation Agentic AI',
		name: 'Formation Agentic AI',
		start: '2026-06-15',
		end: '2026-06-16',
		allDay: true,
		type: 'course',
		location: 'Clevory Academy',
		description:
			'Formation Intelligence Artificielle et conception d’agents IA.',
		image:
			'/assets/images/events-landing-page/formation intellignece artificielle.png',
		link: '/formations/agentic-ia',
	},
	{
		id: 'it-sales-main',
		title: 'Formation IT SALES',
		name: 'Formation IT SALES',
		start: '2026-09-15',
		end: '2026-09-16',
		allDay: true,
		type: 'course',
		location: 'Clevory Academy',
		description:
			'Formation IT Sales pour apprendre à vendre des solutions technologiques.',
		image: '/assets/images/events-landing-page/IT SALES.png',
		link: '/formations/it-sales',
	},
];

const normalizeText = (value = '') =>
	value
		.toString()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const cleanText = (value = '') => value.replace(/\s+/g, ' ').trim();

const escapeRegExp = (value = '') =>
	value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const removeSearchHighlights = () => {
	document.querySelectorAll('mark.search-highlight').forEach((mark) => {
		const parent = mark.parentNode;
		if (!parent) return;
		parent.replaceChild(document.createTextNode(mark.textContent), mark);
		parent.normalize();
	});
};

const removeUnwantedSearchContent = (root) => {
	root
		.querySelectorAll(
			`
			header, nav, footer, script, style, noscript, iframe, svg,
			button, input, textarea,
			.navbar, .navbar-backdrop,
			.breadcrumb,
			[class*="breadcrumb"], [class*="Breadcrumb"],
			[class*="navbar"], [class*="Navbar"],
			[class*="menu"], [class*="Menu"]
		`,
		)
		.forEach((element) => element.remove());
};

const extractPageTitle = (root, fallbackTitle = 'Résultat') => {
	const selectors = [
		'[class*="hero"] h1',
		'[class*="Hero"] h1',
		'.hero h1',
		'h1',
		'[class*="hero"] [class*="title"]',
		'[class*="Hero"] [class*="Title"]',
	];

	for (const selector of selectors) {
		const element = root.querySelector(selector);
		const text = cleanText(element?.innerText || element?.textContent || '');

		if (text && text.length > 2) return text;
	}

	return fallbackTitle;
};

const isBadSearchText = (text) => {
	const normalized = normalizeText(text);

	const blockedExact = [
		'contact',
		'formations',
		'nos formations',
		'accueil',
		'home',
		'academy',
		'calendrier',
		"s'inscrire",
		'whatsapp',
	];

	if (blockedExact.includes(normalized)) return true;
	if (/^(home|accueil)\s*\//i.test(text)) return true;
	if (normalized.includes('home /')) return true;
	if (normalized.includes('accueil /')) return true;

	return false;
};

const getSearchableBlocks = (root) => {
	const unique = new Map();

	const selectors = [
		'p',
		'li',
		'h2',
		'h3',
		'h4',
		'[class*="description"]',
		'[class*="Description"]',
		'[class*="subtitle"]',
		'[class*="Subtitle"]',
		'[class*="text"]',
		'[class*="Text"]',
		'[class*="content"]',
		'[class*="Content"]',
		'[class*="program"]',
		'[class*="Program"]',
	];

	root.querySelectorAll(selectors.join(',')).forEach((element) => {
		if (
			element.closest('header') ||
			element.closest('nav') ||
			element.closest('footer') ||
			element.closest('.navbar') ||
			element.closest('.navbar-backdrop') ||
			element.closest('[class*="breadcrumb"]') ||
			element.closest('[class*="Breadcrumb"]')
		) {
			return;
		}

		const hasNestedBlock =
			element.querySelector('p, li, h2, h3, h4') &&
			!['P', 'LI', 'H2', 'H3', 'H4'].includes(element.tagName);

		if (hasNestedBlock) return;

		const text = cleanText(element.innerText || element.textContent || '');
		const key = normalizeText(text);

		if (text.length < 12) return;
		if (isBadSearchText(text)) return;

		if (!unique.has(key)) {
			unique.set(key, text);
		}
	});

	if (unique.size === 0) {
		const fallbackText = cleanText(root.innerText || root.textContent || '');
		fallbackText
			.split(/(?<=[.!?])\s+|\n+/)
			.map(cleanText)
			.filter((text) => text.length >= 25 && !isBadSearchText(text))
			.forEach((text) => {
				const key = normalizeText(text);
				if (!unique.has(key)) unique.set(key, text);
			});
	}

	return Array.from(unique.values());
};

const findOccurrencesInBlocks = (blocks, query) => {
	const cleanQuery = query.trim();
	const normalizedQuery = normalizeText(cleanQuery);

	if (!normalizedQuery) return [];

	const uniqueBlocks = new Map();

	blocks.forEach((block) => {
		const normalizedBlock = normalizeText(block);
		let index = normalizedBlock.indexOf(normalizedQuery);
		let count = 0;
		const firstIndex = index;

		while (index !== -1) {
			count += 1;
			index = normalizedBlock.indexOf(
				normalizedQuery,
				index + normalizedQuery.length,
			);
		}

		if (count > 0 && !uniqueBlocks.has(normalizedBlock)) {
			uniqueBlocks.set(normalizedBlock, {
				index: firstIndex,
				snippet: block,
				count,
			});
		}
	});

	return Array.from(uniqueBlocks.values());
};

const highlightSnippetParts = (text, query) => {
	const cleanQuery = query.trim();
	if (!cleanQuery) return text;

	const regex = new RegExp(`(${escapeRegExp(cleanQuery)})`, 'gi');

	return text.split(regex).map((part, index) =>
		normalizeText(part) === normalizeText(cleanQuery) ? (
			<mark key={index} className='navbar-search__inline-mark'>
				{part}
			</mark>
		) : (
			<span key={index}>{part}</span>
		),
	);
};

const highlightSearchText = (query) => {
	const cleanQuery = query?.trim();
	if (!cleanQuery) return;

	removeSearchHighlights();

	const root =
		document.querySelector('main') ||
		document.querySelector('#root') ||
		document.body;
	const regex = new RegExp(`(${escapeRegExp(cleanQuery)})`, 'gi');

	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
		acceptNode(node) {
			const parent = node.parentElement;
			if (!parent) return NodeFilter.FILTER_REJECT;

			if (
				[
					'SCRIPT',
					'STYLE',
					'NOSCRIPT',
					'INPUT',
					'TEXTAREA',
					'BUTTON',
					'NAV',
					'HEADER',
					'FOOTER',
					'MARK',
				].includes(parent.tagName) ||
				parent.closest('.navbar') ||
				parent.closest('.navbar-backdrop')
			) {
				return NodeFilter.FILTER_REJECT;
			}

			regex.lastIndex = 0;
			return regex.test(node.textContent)
				? NodeFilter.FILTER_ACCEPT
				: NodeFilter.FILTER_REJECT;
		},
	});

	const textNodes = [];

	while (walker.nextNode()) {
		textNodes.push(walker.currentNode);
	}

	textNodes.forEach((node) => {
		const fragment = document.createDocumentFragment();
		const parts = node.textContent.split(regex);

		parts.forEach((part) => {
			if (normalizeText(part) === normalizeText(cleanQuery)) {
				const mark = document.createElement('mark');
				mark.className = 'search-highlight';
				mark.textContent = part;
				fragment.appendChild(mark);
			} else {
				fragment.appendChild(document.createTextNode(part));
			}
		});

		node.parentNode.replaceChild(fragment, node);
	});

	document.querySelector('mark.search-highlight')?.scrollIntoView({
		behavior: 'smooth',
		block: 'center',
	});
};

const loadPageDataWithIframe = (page) =>
	new Promise((resolve) => {
		const iframe = document.createElement('iframe');

		iframe.src = page.path;
		iframe.title = 'search-page-loader';
		iframe.style.position = 'fixed';
		iframe.style.left = '-9999px';
		iframe.style.top = '-9999px';
		iframe.style.width = '1px';
		iframe.style.height = '1px';
		iframe.style.opacity = '0';
		iframe.style.pointerEvents = 'none';

		const cleanup = () => iframe.remove();

		const timeout = window.setTimeout(() => {
			cleanup();
			resolve({
				title: page.fallbackTitle,
				blocks: [],
			});
		}, 7000);

		iframe.onload = () => {
			window.setTimeout(() => {
				try {
					const doc = iframe.contentDocument || iframe.contentWindow.document;
					const clonedBody = doc.body.cloneNode(true);

					const title = extractPageTitle(doc.body, page.fallbackTitle);

					removeUnwantedSearchContent(clonedBody);

					const main =
						clonedBody.querySelector('main') ||
						clonedBody.querySelector('#root') ||
						clonedBody;

					const blocks = getSearchableBlocks(main);

					window.clearTimeout(timeout);
					cleanup();

					resolve({
						title,
						blocks,
					});
				} catch (error) {
					window.clearTimeout(timeout);
					cleanup();

					resolve({
						title: page.fallbackTitle,
						blocks: [],
					});
				}
			}, 1200);
		};

		document.body.appendChild(iframe);
	});

export default function Navbar() {
	const [scrolled, setScrolled] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [isPreInscriptionOpen, setIsPreInscriptionOpen] = useState(false);
	const [formationALaCarteEvents, setFormationALaCarteEvents] = useState([]);

	const [searchQuery, setSearchQuery] = useState('');
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchExpanded, setSearchExpanded] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);
	const [searchResults, setSearchResults] = useState([]);

	const searchCacheRef = useRef({});
	const searchRef = useRef(null);

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		const handleScroll = () => setScrolled(window.scrollY > 70);
		handleScroll();
		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		let isMounted = true;

		const loadFormationALaCarteEvents = async () => {
			try {
				const data = await getFormationALaCarteTrainings();
				if (isMounted)
					setFormationALaCarteEvents(Array.isArray(data) ? data : []);
			} catch (error) {
				console.error(
					'Erreur lors du chargement des formations à la carte:',
					error,
				);
				if (isMounted) setFormationALaCarteEvents([]);
			}
		};

		loadFormationALaCarteEvents();

		return () => {
			isMounted = false;
		};
	}, []);

	const allCalendarEvents = useMemo(
		() => [...MAIN_COURSE_EVENTS, ...formationALaCarteEvents],
		[formationALaCarteEvents],
	);

	useEffect(() => {
		setMenuOpen(false);
		setSearchOpen(false);
		removeSearchHighlights();
	}, [location.pathname]);

	useEffect(() => {
		if (!searchQuery.trim()) {
			setSearchResults([]);
			setSearchLoading(false);
			removeSearchHighlights();
			sessionStorage.removeItem('clevory-search-highlight');
		}
	}, [searchQuery]);

	useEffect(() => {
		document.body.style.overflow =
			menuOpen || isPreInscriptionOpen ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [menuOpen, isPreInscriptionOpen]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				setMenuOpen(false);
				setIsPreInscriptionOpen(false);
				setSearchOpen(false);
				setSearchExpanded(false);
				removeSearchHighlights();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 991) setMenuOpen(false);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (searchRef.current && !searchRef.current.contains(event.target)) {
				setSearchOpen(false);
				if (!searchQuery.trim()) setSearchExpanded(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [searchQuery]);

	useEffect(() => {
		const savedSearch = sessionStorage.getItem('clevory-search-highlight');
		if (!savedSearch) return;

		try {
			const parsed = JSON.parse(savedSearch);

			if (parsed?.query && parsed?.path === location.pathname) {
				setTimeout(() => highlightSearchText(parsed.query), 500);
				setTimeout(() => highlightSearchText(parsed.query), 1200);
			}
		} catch (error) {
			console.error('Erreur highlight recherche:', error);
		}
	}, [location.pathname]);

	const loadPageData = useCallback(
		async (page) => {
			const cacheKey = page.path;

			if (page.path !== location.pathname && searchCacheRef.current[cacheKey]) {
				return searchCacheRef.current[cacheKey];
			}

			let pageData;

			if (page.path === location.pathname) {
				const clonedBody = document.body.cloneNode(true);
				const title = extractPageTitle(document.body, page.fallbackTitle);

				removeUnwantedSearchContent(clonedBody);

				const main =
					clonedBody.querySelector('main') ||
					clonedBody.querySelector('#root') ||
					clonedBody;

				pageData = {
					title,
					blocks: getSearchableBlocks(main),
				};
			} else {
				pageData = await loadPageDataWithIframe(page);
			}

			searchCacheRef.current[cacheKey] = pageData;
			return pageData;
		},
		[location.pathname],
	);

	const runSearch = useCallback(
		async (queryValue = searchQuery) => {
			const cleanQuery = queryValue.trim();

			if (!cleanQuery) {
				setSearchResults([]);
				setSearchLoading(false);
				return;
			}

			setSearchExpanded(true);
			setSearchOpen(true);
			setSearchLoading(true);

			const pagesResults = await Promise.all(
				SEARCHABLE_PAGES.map(async (page) => {
					const pageData = await loadPageData(page);
					const occurrences = findOccurrencesInBlocks(
						pageData.blocks,
						cleanQuery,
					);

					if (!occurrences.length) return null;

					const totalCount = occurrences.reduce(
						(total, item) => total + item.count,
						0,
					);

					return {
						...page,
						title: pageData.title,
						count: totalCount,
						occurrences: occurrences.slice(0, 5),
						description: occurrences[0]?.snippet || '',
						score: totalCount,
					};
				}),
			);

			const sortedResults = pagesResults
				.filter(Boolean)
				.sort((a, b) => b.score - a.score)
				.slice(0, MAX_SEARCH_RESULTS);

			setSearchResults(sortedResults);
			setSearchLoading(false);
		},
		[searchQuery, loadPageData],
	);

	useEffect(() => {
		const cleanQuery = searchQuery.trim();
		if (!cleanQuery) return;

		setSearchExpanded(true);
		setSearchOpen(true);

		const debounce = window.setTimeout(() => {
			runSearch(cleanQuery);
		}, 260);

		return () => window.clearTimeout(debounce);
	}, [searchQuery, runSearch]);

	const handleSearchButtonClick = () => {
		setSearchExpanded(true);
		setSearchOpen(true);

		window.setTimeout(() => {
			document.querySelector('.navbar-search__input')?.focus();
		}, 50);

		const cleanQuery = searchQuery.trim();
		if (!cleanQuery) return;

		runSearch(cleanQuery);

		sessionStorage.setItem(
			'clevory-search-highlight',
			JSON.stringify({
				query: cleanQuery,
				path: location.pathname,
			}),
		);

		window.setTimeout(() => {
			highlightSearchText(cleanQuery);
		}, 120);
	};

	const handleSearchResultClick = (result) => {
		const cleanQuery = searchQuery.trim();

		sessionStorage.setItem(
			'clevory-search-highlight',
			JSON.stringify({
				query: cleanQuery,
				path: result.path,
			}),
		);

		setSearchOpen(false);
		setSearchExpanded(false);
		setMenuOpen(false);

		if (result.path === location.pathname) {
			window.setTimeout(() => highlightSearchText(cleanQuery), 120);
			return;
		}

		navigate(result.path);
	};

	const logoSrc = '/assets/images/logo-fond-clair.png';

	const closeMenu = () => setMenuOpen(false);
	const toggleMenu = () => setMenuOpen((prev) => !prev);

	const openPreInscriptionModal = () => {
		closeMenu();
		setIsPreInscriptionOpen(true);
	};

	const closePreInscriptionModal = () => setIsPreInscriptionOpen(false);

	const formationsSubmenu = [
		{ label: 'Agentic IA', to: '/formations/agentic-ia' },
		{ label: 'IT sales', to: '/formations/it-sales' },
		{
			label: 'FORMATION À LA CARTE',
			to: {
				pathname: FORMATION_A_LA_CARTE_URI,
				search: '?open=calendar',
				hash: '#course-calendar-section',
			},
			state: {
				openCalendar: true,
				calendarEvents: formationALaCarteEvents,
			},
			isFormationALaCarte: true,
		},
	];

	const renderSearchResults = () => {
		if (searchLoading) {
			return (
				<div className='navbar-search__empty'>Recherche intelligente...</div>
			);
		}

		if (searchResults.length > 0) {
			return searchResults.map((result) => {
				const extraSnippets = result.occurrences
					.map((occurrence) => occurrence.snippet)
					.filter(
						(snippet, index, array) =>
							normalizeText(snippet) !== normalizeText(result.description) &&
							array.findIndex(
								(item) => normalizeText(item) === normalizeText(snippet),
							) === index,
					)
					.slice(0, 4);

				return (
					<button
						key={result.path}
						type='button'
						className='navbar-search__result'
						onClick={() => handleSearchResultClick(result)}>
						<span className='navbar-search__topline'>
							<span className='navbar-search__title'>{result.title}</span>
							<span className='navbar-search__count'>{result.count}</span>
						</span>

						<span className='navbar-search__description'>
							{highlightSnippetParts(result.description, searchQuery)}
						</span>

						{extraSnippets.length > 0 && (
							<span className='navbar-search__snippets'>
								{extraSnippets.map((snippet, index) => (
									<span key={index}>
										{highlightSnippetParts(snippet, searchQuery)}
									</span>
								))}
							</span>
						)}
					</button>
				);
			});
		}

		if (searchQuery.trim()) {
			return <div className='navbar-search__empty'>Aucun résultat trouvé</div>;
		}

		return (
			<div className='navbar-search__empty'>
				Recherchez un mot ou une phrase
			</div>
		);
	};

	return (
		<>
			<header className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
				<div className='navbar__topbar'>
					<div className='navbar__topbar-container'>
						<div className='navbar__topbar-left'>
							<div className='navbar__lang'>
								<div className='navbar__lang'>
									<button
										type='button'
										className='navbar__lang-link navbar__lang-link--active'>
										FR
									</button>
								</div>
							</div>

							<a
								className='navbar__top-item'
								href='tel:+21629376774
'>
								<Phone size={17} strokeWidth={2.2} />
								<span>(+216) 29 376 774</span>
							</a>

							<a
								className='navbar__top-item navbar__top-item--whatsapp'
								href='https://wa.me/21629376774
'
								target='_blank'
								rel='noreferrer'
								aria-label='WhatsApp'>
								<span>WhatsApp</span>
							</a>
						</div>
					</div>
				</div>

				<div className='navbar__main'>
					<div className='navbar-container'>
						<Link to='/' className='navbar-logo' aria-label='Go to homepage'>
							<img
								className='navbar-logo__image'
								src={logoSrc}
								alt='Clevory Academy Logo'
							/>
						</Link>

						<nav
							id='main-navigation'
							className={`navbar-menu ${menuOpen ? 'active' : ''}`}
							aria-hidden={!menuOpen && window.innerWidth <= 991}>
							<div className='navbar-menu__mobile-header'>
								<span className='navbar-menu__mobile-title'>Menu</span>

								<button
									type='button'
									className='navbar-menu__close'
									onClick={closeMenu}
									aria-label='Fermer le menu'>
									<X size={20} strokeWidth={2.2} />
								</button>
							</div>

							<div className='navbar-menu__mobile-search'>
								<div className='navbar-menu__mobile-search-box'>
									<Search size={18} strokeWidth={2.2} />
									<input
										type='text'
										placeholder='Rechercher...'
										aria-label='Rechercher'
										value={searchQuery}
										onChange={(event) => setSearchQuery(event.target.value)}
										onFocus={() => setSearchOpen(true)}
									/>
								</div>

								{searchOpen && menuOpen && (
									<div className='navbar-search__dropdown navbar-search__dropdown--mobile'>
										{renderSearchResults()}
									</div>
								)}
							</div>

							<NavLink
								to='/qui-sommes-nous'
								onClick={closeMenu}
								className={({ isActive }) =>
									`navbar-link ${isActive ? 'navbar-link--active' : ''}`
								}>
								Academy
							</NavLink>

							<div className='navbar-dropdown navbar-dropdown--mobile-open'>
								<NavLink
									to='/formations'
									onClick={closeMenu}
									className={({ isActive }) =>
										`navbar-link navbar-dropdown__toggle ${
											isActive ? 'navbar-link--active' : ''
										}`
									}>
									Formations
									<span className='navbar-dropdown__arrow'>▾</span>
								</NavLink>

								<div className='navbar-submenu'>
									{formationsSubmenu.map((item, index) => (
										<NavLink
											key={`${item.label}-${index}`}
											to={item.to}
											state={item.state}
											onClick={closeMenu}
											className='navbar-submenu__link'>
											<span className='navbar-submenu__bg' />
											<span className='navbar-submenu__text'>{item.label}</span>
										</NavLink>
									))}
								</div>
							</div>

							<NavLink
								to={{
									pathname: CALENDRIER_URI,
									search: '?open=calendar',
									hash: '#course-calendar-section',
								}}
								state={{
									openCalendar: true,
									calendarEvents: allCalendarEvents,
								}}
								onClick={closeMenu}
								className={({ isActive }) =>
									`navbar-link ${isActive ? 'navbar-link--active' : ''}`
								}>
								Calendrier
							</NavLink>

							<NavLink
								to='/contact'
								onClick={closeMenu}
								className={({ isActive }) =>
									`navbar-link ${isActive ? 'navbar-link--active' : ''}`
								}>
								Contact
							</NavLink>

							<div className='navbar-menu__mobile-top'>
								<a
									className='navbar-menu__mobile-item'
									href='tel:+21629376774
'>
									<Phone size={16} strokeWidth={2.2} />
									<span>(+216) 29 376 774</span>
								</a>

								<a
									className='navbar-menu__mobile-item'
									href='https://wa.me/21629376774
'
									target='_blank'
									rel='noreferrer'>
									<span>WhatsApp</span>
								</a>

								<button
									type='button'
									onClick={openPreInscriptionModal}
									className='navbar-menu__mobile-cta'>
									S'inscrire
								</button>
							</div>
						</nav>

						<div className='navbar-right'>
							<div
								ref={searchRef}
								className={`navbar-search ${
									searchExpanded ? 'navbar-search--open' : ''
								}`}>
								<input
									className='navbar-search__input'
									type='text'
									value={searchQuery}
									onChange={(event) => setSearchQuery(event.target.value)}
									onFocus={() => {
										setSearchExpanded(true);
										setSearchOpen(true);
									}}
									placeholder='Rechercher...'
									aria-label='Rechercher'
								/>

								<button
									type='button'
									className='navbar__desktop-search'
									onClick={handleSearchButtonClick}
									aria-label='Recherche'>
									<Search size={18} strokeWidth={2.2} />
								</button>

								{searchOpen && (
									<div className='navbar-search__dropdown'>
										{renderSearchResults()}
									</div>
								)}
							</div>

							<button
								type='button'
								onClick={openPreInscriptionModal}
								className='navbar-link navbar__desktop-cta'>
								S'inscrire
							</button>

							<button
								type='button'
								className={`menu-toggle ${
									menuOpen ? 'is-open menu-toggle--hidden-mobile' : ''
								}`}
								onClick={toggleMenu}
								aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
								aria-expanded={menuOpen}
								aria-controls='main-navigation'>
								<span />
								<span />
								<span />
							</button>
						</div>
					</div>
				</div>
			</header>

			<button
				type='button'
				className={`navbar-backdrop ${menuOpen ? 'is-visible' : ''}`}
				onClick={closeMenu}
				aria-label='Close menu'
				tabIndex={menuOpen ? 0 : -1}
			/>

			<Suspense fallback={null}>
				{isPreInscriptionOpen && (
					<PreInscriptionModal onClose={closePreInscriptionModal} />
				)}
			</Suspense>
		</>
	);
}
